import { useCallback, useEffect, useRef } from 'react';

/** Default delay: collapse burst of `sync:*` events from one pull into a single refresh. */
const DEFAULT_MS = 120;

/**
 * Schedules a callback after `ms` of quiet time. Multiple rapid calls (e.g. several
 * `publishCrossTab` events from one `pullLatestFromSupabase`) result in one run.
 * Helps slow phones/tablets avoid redundant IndexedDB reads.
 */
export function useCoalescedSyncRefresh(callback: () => void, ms: number = DEFAULT_MS): () => void {
  const cbRef = useRef(callback);
  cbRef.current = callback;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const schedule = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      cbRef.current();
    }, ms);
  }, [ms]);

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  return schedule;
}
